import axios from 'axios';
import oidc from '@/auth/oidcClient';

const SIX_DIGIT_PATH = '/sixDigitAuthorization';

/**
 * The API answers 401 `mfa_required` when the token is valid but its second
 * factor is missing or older than the file endpoints allow (pacmny-api
 * RequireSecondFactor; main: keyon's file.php and a lapsed auth_mfa_session
 * row). The person is sent to verify a fresh second factor.
 *
 * Only object bodies are read here. A file download asks for a Blob, reads its
 * own refusal and calls redirectToCodePage itself (api/keyonFiles.js). Images,
 * iframes and PDF.js fetches never pass through axios at all; those show
 * "Document unavailable" instead, and the next axios call redirects.
 */
export function isMfaRequired(error) {
  const res = error && error.response;
  if (!res || res.status !== 401) return false;
  const { data } = res;
  if (!data || typeof data !== 'object') return false;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return false;
  return data.code === 'mfa_required';
}

// jsdom's Blob (and older Safari's) has no text(); FileReader works everywhere.
function readBlobText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * isMfaRequired for a request that asked for a Blob, whose refusal is a Blob
 * too and so invisible to the interceptor. pacmny-be's downloadCda reads its
 * own with this (api/cdaApi.js).
 * @returns {Promise<boolean>}
 */
export async function isMfaRequiredBlob(error) {
  const res = error && error.response;
  if (!res || res.status !== 401) return false;
  if (typeof Blob === 'undefined' || !(res.data instanceof Blob)) return false;
  try {
    const parsed = JSON.parse(await readBlobText(res.data));
    return Boolean(parsed) && parsed.code === 'mfa_required';
  } catch (_) {
    return false;
  }
}

function currentRoute(router) {
  const route = router && router.currentRoute;
  // vue-router 4 exposes a ref; tolerate a plain object too.
  return (route && 'value' in route ? route.value : route) || {};
}

// The store, router and sign-in main.js installed, for callers that cannot inject them.
let installed = null;

// One sign-in at a time: several refused requests in flight must not each
// start one.
let reauthenticating = false;

/**
 * Back to a fresh second factor, remembering where the user was.
 *
 * On main that was the six-digit code page. In the port the second factor is
 * part of the authorization server's sign-in, so this starts a new sign-in
 * with `prompt=login` - the server asks for the password and a new code
 * rather than reissuing a token from its own session, and refreshing cannot
 * help because a refreshed token carries the same `auth_time` - returning to
 * the current route afterwards. It is the answer api2/client.js gives the same
 * refusal on a native call. The vuex flags main set are still cleared, for
 * the screens that read them.
 * @returns {boolean} whether a sign-in was started
 */
export function redirectToCodePage({ store, router, auth } = installed || {}) {
  const route = currentRoute(router);
  // On main's code page: redirecting again would loop.
  if (route.path === SIX_DIGIT_PATH) return false;
  const provider = auth || (installed && installed.auth) || oidc;
  if (reauthenticating || !provider || typeof provider.beginLogin !== 'function') return false;
  reauthenticating = true;
  const returnTo = route.fullPath || route.path || '/';
  if (store) {
    store.commit('login/SET_2FA_VERIFIED', false);
    store.commit('login/SET_INTENDED_ROUTE', returnTo);
  }
  try {
    if (typeof provider.clearSession === 'function') provider.clearSession();
    const started = provider.beginLogin({ returnTo, prompt: 'login' });
    if (started && typeof started.catch === 'function') {
      // The provider is unreachable: let a later refusal try again.
      started.catch(() => { reauthenticating = false; });
    }
  } catch (_) {
    reauthenticating = false;
    return false;
  }
  return true;
}

/** Registers one response interceptor; every error is rethrown to its caller. */
export function installMfaRequiredHandler(client = axios, { store, router, auth } = {}) {
  installed = { store, router, auth };
  reauthenticating = false;
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isMfaRequired(error)) redirectToCodePage({ store, router });
      return Promise.reject(error);
    },
  );
}
